import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ToastContainer';

const FeedbackWidget: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [ease, setEase] = useState(0);
  const [hoveredEase, setHoveredEase] = useState(0);
  const [formData, setFormData] = useState({
    workingWell: '',
    needsImprovement: '',
    wouldRecommend: '',
    recommendReason: '',
    canFeature: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      showError('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating,
          ...formData,
          userEmail: user?.email,
          userName: `${user?.firstName} ${user?.lastName}`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      showSuccess('Thank you! Your feedback helps us improve Svarra 🎉');
      setIsOpen(false);
      // Reset form
      setRating(0);
      setFormData({
        workingWell: '',
        needsImprovement: '',
        wouldRecommend: '',
        recommendReason: '',
        canFeature: false,
      });
    } catch (error) {
      console.error('Feedback submission error:', error);
      showError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <>
      {/* Floating Button - Matches Svarra Advisor Bot style */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[90px] right-5 md:bottom-5 md:right-[195px] z-[1000] flex items-center bg-gradient-to-br from-orange-500 to-red-500 text-white border-none rounded-[25px] px-5 py-3 cursor-pointer shadow-[0_8px_25px_rgba(249,115,22,0.3)] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-[0_12px_35px_rgba(249,115,22,0.4)] max-w-[200px]"
          aria-label="Give feedback"
        >
          <div className="text-2xl mr-3 flex items-center justify-center">
            📝
          </div>
          <div className="text-left leading-tight">
            <div className="font-semibold text-sm">Give Feedback</div>
            <div className="text-xs opacity-90">Help us improve</div>
          </div>
        </button>
      )}

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <span>📝</span> Share Your Feedback
                  </h2>
                  <p className="text-orange-100 text-sm mt-1">
                    Help us make Svarra better for you!
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                  aria-label="Close feedback form"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  How would you rate your experience with Svarra? *
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="text-4xl transition-transform hover:scale-125 focus:outline-none"
                    >
                      {(hoveredRating || rating) >= star ? '⭐' : '☆'}
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-3 text-sm font-medium text-gray-600 dark:text-gray-400">
                      {rating === 5 && '🎉 Amazing!'}
                      {rating === 4 && '😊 Great!'}
                      {rating === 3 && '👍 Good'}
                      {rating === 2 && '😐 Okay'}
                      {rating === 1 && '😟 Needs work'}
                    </span>
                  )}
                </div>
              </div>

              {/* Ease of Use Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  How easy was it to deploy and use Svarra? *
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEase(star)}
                      onMouseEnter={() => setHoveredEase(star)}
                      onMouseLeave={() => setHoveredEase(0)}
                      className="text-4xl transition-transform hover:scale-125 focus:outline-none"
                    >
                      {(hoveredEase || ease) >= star ? '⭐' : '☆'}
                    </button>
                  ))}

                  {ease > 0 && (
                    <span className="ml-3 text-sm font-medium text-gray-600 dark:text-gray-400">
                      {ease === 5 && '🚀 Super easy!'}
                      {ease === 4 && '😄 Pretty easy'}
                      {ease === 3 && '🙂 Manageable'}
                      {ease === 2 && '😕 Took some effort'}
                      {ease === 1 && '😣 Hard to use'}
                    </span>
                  )}
                </div>
              </div>

              {/* What's working well */}
              <div>
                <label htmlFor="workingWell" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  What's working well for you?
                </label>
                <textarea
                  id="workingWell"
                  name="workingWell"
                  value={formData.workingWell}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder="Tell us what you love about Svarra..."
                />
              </div>

              {/* What needs improvement */}
              <div>
                <label htmlFor="needsImprovement" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  What could we improve?
                </label>
                <textarea
                  id="needsImprovement"
                  name="needsImprovement"
                  value={formData.needsImprovement}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder="Share ideas, bugs, or features you'd like to see..."
                />
              </div>

              {/* Would you recommend */}
              <div>
                <label htmlFor="wouldRecommend" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Would you recommend Svarra to other businesses?
                </label>
                <select
                  id="wouldRecommend"
                  name="wouldRecommend"
                  value={formData.wouldRecommend}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select an option...</option>
                  <option value="definitely">Yes, definitely!</option>
                  <option value="probably">Probably</option>
                  <option value="not-sure">Not sure yet</option>
                  <option value="probably-not">Probably not</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* Why recommend/not recommend */}
              {formData.wouldRecommend && (
                <div>
                  <label htmlFor="recommendReason" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Why or why not?
                  </label>
                  <textarea
                    id="recommendReason"
                    name="recommendReason"
                    value={formData.recommendReason}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    placeholder="Tell us more..."
                  />
                </div>
              )}

              {/* Permission to feature */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <input
                  type="checkbox"
                  id="canFeature"
                  name="canFeature"
                  checked={formData.canFeature}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="canFeature" className="text-sm text-gray-700 dark:text-gray-300">
                  I give Svarra permission to feature my feedback as a testimonial on your website and marketing materials.
                  (Your name and business will be included unless you tell us otherwise)
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || rating === 0}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Feedback'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
